import { add, dot, flatBasis } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, aim as aimFn, applyHeal, applyStatus, hitscan, isEnemy, moveProjectile, spawnZone } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { pk, zk, type HeroModule } from "../module.ts";
import { action, airHold, alliesIn, commonInput, consume, jetpack, launchProjectile, offWall, ownerAlive, ready, reviveAt, secs, spreadDir, startMove, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { fuel: 0, climbing: 1 } as const;
export const PK = { RepairOrb: pk(HeroId.PocketRaccoon, 0) } as const;
export const ZK = { Beacon: zk(HeroId.PocketRaccoon, 0), Amplifier: zk(HeroId.PocketRaccoon, 1) } as const;

const def: HeroDef = {
  id: HeroId.PocketRaccoon,
  slug: "pocket-raccoon",
  name: "Pocket Raccoon",
  role: Role.Support,
  color: "#ffd84a",
  blurb: "Tiny jetpack mechanic with a rapid blaster, bouncing repair orbs, revive beacon, and team amplifier.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.75,
  headRadius: 0.2,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 45,
  reloadTime: 1.1,
  ultCost: 4300,
  abilities: {
    [Slot.Primary]: { name: "Bombard", key: "LMB", description: "Rapid-fire energy rounds with headshot damage.", cooldown: 0 },
    [Slot.Secondary]: { name: "Repair Orbs", key: "RMB", description: "Fire bouncing orbs that burst-heal allies on direct hits.", cooldown: 0 },
    [Slot.Ability1]: { name: "Jet Dash", key: "Shift", description: "Dash 8m in the direction you're moving. Stores two charges.", cooldown: 10, charges: 2 },
    [Slot.Ability2]: { name: "Rebirth Beacon", key: "E", description: "Drop a long-lived beacon that revives a fallen ally at it and hands out 25-point armor packs.", cooldown: 40 },
    [Slot.Ultimate]: { name: "Amplifier", key: "Q", description: "Aim, then LMB to deploy a 12s amplifier: allies near it deal 40% more damage and build up to 150 bonus health.", cooldown: 0 },
  },
  passive: { name: "Wall Scamper", description: "Hold Space against a wall to climb it; hold Space in the air to fly briefly." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.fuel, 100);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) spawnZone(sim, eid, ZK.Amplifier, targetPoint(sim, eid, Slot.Ultimate).pos, 20, 12);
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        const d = wishDir(input);
        const dashTicks = secs(0.433);
        const dashSpeed = 8 / (dashTicks / 60);
        startMove(sim, eid, MoveKind.Dash, dashTicks, dashSpeed, { x: d.x * dashSpeed, y: 0, z: d.z * dashSpeed });
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 15);
        spawnZone(sim, eid, ZK.Beacon, sim.posOf(eid), 50, 300);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = 5;
        action(sim, eid, ActionKind.Fire, 5);
        const aim = awaitAim(sim, eid);
        const dir = spreadDir(aim.dir, 0.01, c.Weapon.shots[eid]! * 97 + c.Net.id[eid]!);
        hitscan(sim, eid, aim.origin, dir, { range: 70, damage: 17, falloffStart: 10, falloffEnd: 20, falloffMin: 0.4, canCrit: true, fxKind: FxKind.Pistol });
      } else if (input.buttons & Btn.Secondary) {
        if (trigger(sim, eid, true)) {
          c.Weapon.fireCd[eid] = secs(1 / 1.8);
          action(sim, eid, ActionKind.AltFire, 6);
          launchProjectile(sim, eid, PK.RepairOrb, 80, input, { radius: 0.18, seconds: 7 });
        }
      }
    },
    movement(sim, eid, input, mods) {
      const c = sim.c;
      const { fwd } = flatBasis(input.yaw);
      const chest = add(sim.posOf(eid), { x: 0, y: 0.9, z: 0 });
      const climbing = !!(input.buttons & Btn.Jump) && input.moveZ > 0.3 && sim.physics.raycast(chest, fwd, sim.hero(eid).radius + 0.35) >= 0;
      setHv(c, eid, V.climbing, climbing ? 1 : 0);
      if (climbing) {
        c.Vel.y[eid] = 8;
        mods.gravityMul = 0;
        mods.canJump = false;
        return;
      }
      jetpack(sim, eid, mods, V.fuel, { want: airHold(sim, eid, input, 2), drain: 40, regenGround: 40, regenAir: 0, lift: 9, maxUp: 5 });
    },
  },
  targets: {
    [Slot.Ultimate]: { range: 20, clearance: 0.5, radius: 20, verb: "Place" },
  },
  projectiles: {
    [PK.RepairOrb]: {
      visual: { color: "#7dffb0", size: 0.16, trail: true },
      tick(sim, p) {
        const c = sim.c;
        const owner = c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "allies");
        if (hit) {
          if (hit.target !== undefined) {
            if (ownerAlive(sim, owner)) {
              applyHeal(sim, owner, hit.target, 55);
              addOverhealth(sim, hit.target, 25, secs(1.5), owner);
            }
            sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
            sim.destroyEntity(p);
            return;
          }
          if (hit.map && hit.normal && c.Projectile.bounces[p]! < 10) {
            const v = { x: c.Vel.x[p]!, y: c.Vel.y[p]!, z: c.Vel.z[p]! };
            const k = 2 * dot(v, hit.normal);
            c.Vel.x[p] = (v.x - k * hit.normal.x) * 0.85;
            c.Vel.y[p] = (v.y - k * hit.normal.y) * 0.85;
            c.Vel.z[p] = (v.z - k * hit.normal.z) * 0.85;
            const out = offWall(hit, 0.05);
            c.Pos.x[p] = out.x;
            c.Pos.y[p] = out.y;
            c.Pos.z[p] = out.z;
            c.Projectile.bounces[p] = c.Projectile.bounces[p]! + 1;
          } else {
            sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
            sim.destroyEntity(p);
            return;
          }
        }
        if (c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
  zones: {
    [ZK.Beacon]: {
      visual: { color: "#7dffb0", dome: false },
      tick(sim, z) {
        const c = sim.c;
        const owner = c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        const age = c.Zone.age[z]!;
        if (age === secs(3.4)) {
          const fallen = sim.playerEids().find((o) => !c.Player.alive[o] && !isEnemy(sim, owner, o) && sim.players.get(o)?.deathPos);
          if (fallen !== undefined) reviveAt(sim, fallen, sim.posOf(z), owner);
        }
        if (age % 180 === 1) {
          sim.emit({ type: "pickup", pos: sim.posOf(z), large: false });
          for (const e of alliesIn(sim, z)) addOverhealth(sim, e, 25, secs(300), owner);
        }
      },
    },
    [ZK.Amplifier]: {
      visual: { color: "#ffd84a", dome: true },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        for (const e of alliesIn(sim, z, 1)) {
          applyStatus(sim, e, "boost", 0.2, { source: owner, boostPct: 0.4 });
          addOverhealth(sim, e, Math.min(150, sim.c.Health.over[e]! + 100 / 60), secs(1), owner);
        }
      },
    },
  },
};

function awaitAim(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number): ReturnType<typeof aimFn> {
  return aimFn(sim, eid);
}

export default module;
